import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Shield,
  X,
  Check,
  Save,
  Copy,
  Search,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ROLES = [
  { key: "owner", label: "Owner" },
  { key: "admin", label: "Admin" },
  { key: "member", label: "Member" },
  { key: "viewer", label: "Viewer" },
];
const VAL_OPTIONS = [
  { value: "yes", label: "Yes", bg: "bg-mint-100", text: "text-emerald-700" },
  { value: "maybe", label: "Maybe", bg: "bg-butter-100", text: "text-amber-700" },
  { value: "no", label: "No", bg: "bg-rose-100", text: "text-rose-600" },
];
const VAL_META = Object.fromEntries(VAL_OPTIONS.map((o) => [o.value, o]));

const ROW_GRID = "24px 1fr 110px 110px 110px 110px 60px";

// Cute Airtable-style chip palette — deterministic per chip label
const CHIP_PALETTE = [
  "bg-sky-100 text-sky-700",
  "bg-mint-100 text-emerald-700",
  "bg-peach-100 text-amber-800",
  "bg-lilac-100 text-purple-700",
  "bg-butter-100 text-amber-700",
  "bg-rose-100 text-rose-600",
];
function chipColor(label) {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) % 1000003;
  return CHIP_PALETTE[h % CHIP_PALETTE.length];
}

function ValuePill({ value, onChange, testid }) {
  const m = VAL_META[value] || VAL_META["no"];
  return (
    <Select value={value || "no"} onValueChange={onChange}>
      <SelectTrigger
        className={`h-8 px-2 rounded-md text-xs font-medium border-0 focus:ring-1 ${m.bg} ${m.text}`}
        data-testid={testid}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {VAL_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className={`inline-block px-2 py-0.5 rounded-md text-xs ${o.bg} ${o.text}`}>
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- Multi-select chip dropdown for sub-agents (Airtable-style) ---
function MultiSelectChips({ selected, options, allLabel = "All sub-agents", onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const click = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const allMode = (selected || []).length === 0;
  const visibleOptions = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()) && !(selected || []).includes(o));

  const toggle = (val) => {
    if (val === "__all__") return onChange([]);
    const next = (selected || []).includes(val) ? selected.filter((s) => s !== val) : [...(selected || []), val];
    onChange(next);
  };
  const remove = (val) => onChange((selected || []).filter((s) => s !== val));

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div
        onClick={() => setOpen(true)}
        className="min-h-[40px] w-full rounded-xl border border-slate-200 bg-white px-2 py-1.5 flex flex-wrap items-center gap-1.5 cursor-text focus-within:border-slate-300"
      >
        {allMode ? (
          <span className="inline-flex h-7 items-center px-2.5 rounded-md text-xs font-medium bg-slate-900 text-white">
            {allLabel}
          </span>
        ) : (
          (selected || []).map((s) => (
            <span key={s} className={`inline-flex h-7 items-center gap-1 px-2.5 rounded-md text-xs font-medium ${chipColor(s)}`}>
              {s || "—"}
              <button
                onClick={(e) => { e.stopPropagation(); remove(s); }}
                className="opacity-60 hover:opacity-100"
                data-testid={`chip-remove-${s}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={allMode ? "" : "Type to search…"}
          onFocus={() => setOpen(true)}
          className="flex-1 min-w-[120px] h-7 outline-none text-sm bg-transparent placeholder:text-slate-400"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          <button
            onClick={() => { toggle("__all__"); setOpen(false); }}
            className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100"
            data-testid={`${testid}-option-all`}
          >
            <span className={`h-4 w-4 rounded-[4px] flex items-center justify-center ${allMode ? "bg-slate-900 text-white" : "border border-slate-300 bg-white"}`}>
              {allMode && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="text-sm font-medium text-slate-800">{allLabel}</span>
          </button>
          {visibleOptions.length === 0 && !allMode ? (
            <div className="px-3 py-3 text-xs text-slate-400">No matches</div>
          ) : (
            visibleOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => { toggle(opt); setQuery(""); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                data-testid={`${testid}-option-${opt}`}
              >
                <span className={`inline-flex h-6 items-center px-2 rounded-md text-xs font-medium ${chipColor(opt)}`}>
                  {opt || "—"}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// --- Permission table inside a group ---
function PermissionRowsTable({ rows, readOnly, onUpdateRow, onAddRow, onRemoveRow }) {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <div
        className="grid items-center gap-2 px-3 h-10 bg-slate-100/70 border-b border-slate-200 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700"
        style={{ gridTemplateColumns: ROW_GRID }}
      >
        <div></div>
        <div>Permission</div>
        <div>Owner</div>
        <div>Admin</div>
        <div>Member</div>
        <div>Viewer</div>
        <div></div>
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-5 text-center text-xs text-slate-400">
          {readOnly ? "No rows in this template." : "No rows yet — click \"Add row\""}
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={row.id}
            className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 hover:bg-slate-50/40 group"
            style={{ gridTemplateColumns: ROW_GRID }}
            data-testid={`pg-row-${row.id}`}
          >
            <div className="text-slate-300"><GripVertical className="h-4 w-4" /></div>
            {readOnly ? (
              <span className="text-sm font-medium text-slate-800 truncate">{row.name || "—"}</span>
            ) : (
              <Input
                defaultValue={row.name}
                onBlur={(e) => { if (e.target.value !== row.name) onUpdateRow(row.id, { name: e.target.value }); }}
                placeholder="Permission name"
                className="h-8 text-sm"
                data-testid={`pg-row-name-${row.id}`}
              />
            )}
            {ROLES.map((r) => (
              readOnly ? (
                <span
                  key={r.key}
                  className={`inline-flex h-7 px-2 rounded-md items-center text-xs font-medium ${VAL_META[row[r.key]]?.bg || "bg-slate-100"} ${VAL_META[row[r.key]]?.text || "text-slate-600"}`}
                >
                  {VAL_META[row[r.key]]?.label || "—"}
                </span>
              ) : (
                <ValuePill
                  key={r.key}
                  value={row[r.key]}
                  onChange={(v) => onUpdateRow(row.id, { [r.key]: v })}
                  testid={`pg-row-${r.key}-${row.id}`}
                />
              )
            ))}
            <div className="flex items-center justify-end">
              {!readOnly && (
                <button
                  onClick={() => onRemoveRow(row.id)}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-all"
                  data-testid={`pg-row-delete-${row.id}`}
                  title="Remove row"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))
      )}
      {!readOnly && (
        <div className="px-3 py-2 border-t border-slate-100">
          <button
            onClick={onAddRow}
            className="h-7 px-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium flex items-center gap-1"
            data-testid="pg-add-row"
          >
            <Plus className="h-3.5 w-3.5" /> Add row
          </button>
        </div>
      )}
    </div>
  );
}

// --- Group card (Default settings or Template) ---
function GroupCard({
  group, subAgentOptions, isTemplate, expanded, onToggleExpand,
  onUpdateGroup, onDelete, onSaveAsTemplate, onUseTemplate,
}) {
  const onAddRow = () => {
    const newRow = {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-5)}`,
      name: "",
      owner: "no",
      admin: "no",
      member: "no",
      viewer: "no",
    };
    onUpdateGroup({ rows: [...(group.rows || []), newRow] });
  };
  const onUpdateRow = (rowId, patch) =>
    onUpdateGroup({ rows: (group.rows || []).map((r) => (r.id === rowId ? { ...r, ...patch } : r)) });
  const onRemoveRow = (rowId) =>
    onUpdateGroup({ rows: (group.rows || []).filter((r) => r.id !== rowId) });

  const rowCount = (group.rows || []).length;
  const allMode = (group.sub_agents || []).length === 0;

  return (
    <div className={`rounded-2xl border ${isTemplate ? "border-purple-200 bg-lilac-50/40" : "border-slate-200 bg-white"}`} data-testid={`pg-card-${group.id}`}>
      <div className="flex items-center gap-3 px-4 min-h-12 py-2 border-b border-slate-100 flex-wrap">
        <button
          onClick={onToggleExpand}
          className="text-slate-400 hover:text-slate-700"
          data-testid={`pg-toggle-${group.id}`}
        >
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        {isTemplate ? (
          <Input
            defaultValue={group.template_name || ""}
            onBlur={(e) => { if (e.target.value !== (group.template_name || "")) onUpdateGroup({ template_name: e.target.value }); }}
            className="h-8 text-sm font-semibold w-72"
            placeholder="Template name"
            data-testid={`pg-tpl-name-${group.id}`}
          />
        ) : (
          <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">Default settings</span>
        )}
        {/* Inline sub-agent chips (or "All sub-agents" if none picked) */}
        <div className="flex flex-wrap items-center gap-1.5">
          {allMode ? (
            <span className="inline-flex h-6 items-center px-2 rounded-md text-[11px] font-medium bg-slate-900 text-white">
              All sub-agents
            </span>
          ) : (
            (group.sub_agents || []).map((s) => (
              <span key={s} className={`inline-flex h-6 items-center px-2 rounded-md text-[11px] font-medium ${chipColor(s)}`}>
                {s || "—"}
              </span>
            ))
          )}
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 whitespace-nowrap">{rowCount} row{rowCount === 1 ? "" : "s"}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {isTemplate ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onUseTemplate}
              className="h-8"
              data-testid={`pg-use-template-${group.id}`}
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Use template
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onSaveAsTemplate}
              className="h-8"
              data-testid={`pg-save-template-${group.id}`}
            >
              <Save className="h-3.5 w-3.5 mr-1" /> Save as template
            </Button>
          )}
          <button
            onClick={onDelete}
            className="h-8 w-8 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
            data-testid={`pg-delete-${group.id}`}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Sub-agents picker */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">Sub-agents</p>
            <MultiSelectChips
              selected={group.sub_agents || []}
              options={subAgentOptions}
              allLabel="All sub-agents"
              onChange={(next) => onUpdateGroup({ sub_agents: next })}
              testid={`pg-subagents-${group.id}`}
            />
          </div>

          {/* Permission rows table */}
          <PermissionRowsTable
            rows={group.rows || []}
            readOnly={false}
            onUpdateRow={onUpdateRow}
            onAddRow={onAddRow}
            onRemoveRow={onRemoveRow}
          />
        </div>
      )}
    </div>
  );
}

function SortableTab({ id, label, count, active, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`perm-tab-${id}`}
      className={`h-9 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors cursor-grab active:cursor-grabbing ${
        active ? "btn-brand" : "text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
      <span className={`ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded-md ${
        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
      }`}>
        {count}
      </span>
    </button>
  );
}

export default function Permissions() {
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tabOrder, setTabOrder] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  // Save-as-template dialog
  const [tplDlgOpen, setTplDlgOpen] = useState(false);
  const [tplDlgGroupId, setTplDlgGroupId] = useState(null);
  const [tplDlgName, setTplDlgName] = useState("");

  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    try {
      setLoading(true);
      const [a, g, t] = await Promise.all([
        api.get("/agents"),
        api.get("/permission-groups"),
        api.get("/settings/agent-tabs"),
      ]);
      setAgents(a.data);
      setGroups(g.data);
      setTabOrder(Array.isArray(t.data) ? t.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Build agent_name list (ordered)
  const agentList = useMemo(() => {
    const names = Array.from(new Set(agents.map((a) => a.agent_name).filter(Boolean)));
    const orderIndex = new Map(tabOrder.map((n, i) => [n, i]));
    return names.sort((x, y) => {
      const ix = orderIndex.has(x) ? orderIndex.get(x) : Number.POSITIVE_INFINITY;
      const iy = orderIndex.has(y) ? orderIndex.get(y) : Number.POSITIVE_INFINITY;
      if (ix !== iy) return ix - iy;
      return x.localeCompare(y);
    });
  }, [agents, tabOrder]);

  // Default to first agent tab
  useEffect(() => {
    if (!activeTab && agentList.length > 0) setActiveTab(agentList[0]);
  }, [agentList, activeTab]);

  // Sub-agents for the current tab
  const subAgentOptions = useMemo(() => {
    const subSet = new Set();
    for (const a of agents) {
      if (a.agent_name === activeTab && a.sub_agent) subSet.add(a.sub_agent);
    }
    return Array.from(subSet).sort();
  }, [agents, activeTab]);

  const groupsForTab = useMemo(
    () => groups.filter((g) => g.agent_name === activeTab),
    [groups, activeTab],
  );
  const defaultGroups = groupsForTab.filter((g) => !g.is_template);
  const templateGroups = groupsForTab.filter((g) => g.is_template);

  const handleTabDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = agentList.indexOf(active.id);
    const newIdx = agentList.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(agentList, oldIdx, newIdx);
    setTabOrder(next);
    try { await api.put("/settings/agent-tabs", { order: next }); }
    catch { toast.error("Failed to save tab order"); }
  };

  const addDefaultGroup = async () => {
    try {
      const next_order = (defaultGroups.length === 0) ? 0 : Math.max(...defaultGroups.map((g) => g.order || 0)) + 1;
      const { data } = await api.post("/permission-groups", {
        agent_name: activeTab,
        sub_agents: [],
        rows: [],
        order: next_order,
        is_template: false,
      });
      setGroups((p) => [...p, data]);
      setExpanded((e) => ({ ...e, [data.id]: true }));
    } catch { toast.error("Failed to add"); }
  };

  const updateGroup = async (id, patch) => {
    // Optimistic update
    setGroups((p) => p.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    try {
      const { data } = await api.patch(`/permission-groups/${id}`, patch);
      setGroups((p) => p.map((g) => (g.id === id ? data : g)));
    } catch { toast.error("Save failed"); load(); }
  };

  const deleteGroup = async (id) => {
    try {
      await api.delete(`/permission-groups/${id}`);
      setGroups((p) => p.filter((g) => g.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const openSaveTplDlg = (groupId) => {
    setTplDlgGroupId(groupId);
    setTplDlgName("");
    setTplDlgOpen(true);
  };

  const submitSaveTpl = async () => {
    if (!tplDlgName.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post(`/permission-groups/${tplDlgGroupId}/save-as-template`, { template_name: tplDlgName.trim() });
      setGroups((p) => [...p, data]);
      setExpanded((e) => ({ ...e, [data.id]: true }));
      setTplDlgOpen(false);
      toast.success("Saved as template");
    } catch { toast.error("Save failed"); }
  };

  const applyTemplate = async (templateId) => {
    try {
      const { data } = await api.post(`/permission-groups/${templateId}/use-template`);
      setGroups((p) => [...p, data]);
      setExpanded((e) => ({ ...e, [data.id]: true }));
      toast.success("Template applied");
    } catch { toast.error("Failed"); }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" /> Permissions
          </h2>
          <p className="text-sm text-slate-500">Define default permission groups per agent and reuse them as templates.</p>
        </div>
      </div>

      {/* Top tabs (agent_name, draggable) */}
      {!loading && agentList.length > 0 && (
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-2xl p-1 overflow-x-auto" data-testid="permissions-tabs">
          <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
            <SortableContext items={agentList} strategy={horizontalListSortingStrategy}>
              {agentList.map((a) => {
                const count = groups.filter((g) => g.agent_name === a && !g.is_template).length;
                return (
                  <SortableTab
                    key={a}
                    id={a}
                    label={a}
                    count={count}
                    active={activeTab === a}
                    onClick={() => setActiveTab(a)}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-500">Loading…</div>
      ) : agentList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">No agents in your catalog yet — add some in the Agents page first.</p>
        </div>
      ) : (
        <>
          {/* Section 1 — Default settings */}
          <section className="space-y-3" data-testid="default-settings-section">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-slate-900">Default settings</h3>
              <Button onClick={addDefaultGroup} className="btn-brand h-9 rounded-xl" data-testid="pg-add-default">
                <Plus className="h-4 w-4 mr-1" /> Add default settings
              </Button>
            </div>
            {defaultGroups.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                <p className="text-sm text-slate-500">No default settings for <span className="font-semibold">{activeTab}</span> yet.</p>
                <Button onClick={addDefaultGroup} variant="outline" className="mt-3" data-testid="pg-add-default-empty">
                  <Plus className="h-4 w-4 mr-1" /> Add default settings
                </Button>
              </div>
            ) : (
              defaultGroups
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((g) => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    subAgentOptions={subAgentOptions}
                    isTemplate={false}
                    expanded={expanded[g.id] !== false}
                    onToggleExpand={() => toggleExpand(g.id)}
                    onUpdateGroup={(patch) => updateGroup(g.id, patch)}
                    onDelete={() => deleteGroup(g.id)}
                    onSaveAsTemplate={() => openSaveTplDlg(g.id)}
                  />
                ))
            )}
          </section>

          {/* Section 2 — Templates */}
          {templateGroups.length > 0 && (
            <section className="space-y-3" data-testid="templates-section">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold text-slate-900">Templates</h3>
                <span className="text-xs text-slate-500">{templateGroups.length} saved · scoped to {activeTab}</span>
              </div>
              {templateGroups
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((g) => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    subAgentOptions={subAgentOptions}
                    isTemplate={true}
                    expanded={!!expanded[g.id]}
                    onToggleExpand={() => toggleExpand(g.id)}
                    onUpdateGroup={(patch) => updateGroup(g.id, patch)}
                    onDelete={() => deleteGroup(g.id)}
                    onUseTemplate={() => applyTemplate(g.id)}
                  />
                ))}
            </section>
          )}
        </>
      )}

      {/* Save as template dialog */}
      <Dialog open={tplDlgOpen} onOpenChange={setTplDlgOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Save as template</DialogTitle>
            <DialogDescription>Snapshot these rows + sub-agents into a reusable template under <span className="font-medium text-slate-800">{activeTab}</span>.</DialogDescription>
          </DialogHeader>
          <Input
            value={tplDlgName}
            onChange={(e) => setTplDlgName(e.target.value)}
            placeholder="e.g. Strategy team — full access"
            autoFocus
            data-testid="save-template-name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplDlgOpen(false)}>Cancel</Button>
            <Button onClick={submitSaveTpl} className="btn-brand" data-testid="save-template-submit">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
